/**
 * Per-user periodicity overrides (user_requirement_overrides table).
 *
 * The system keeps operating with the definition's renewal_type everywhere.
 * An override only affects: (1) the effective periodicity label shown in the
 * UI, and (2) the approval auto-fill flow, which creates phantom approved
 * documents for the definition-level periods the user is exempt from.
 */

import { supabase } from '../lib/supabase';
import {
  getPeriodStartMonth,
  getExpectedMonthsForYear,
  parseIntervalMonths,
  getPeriodDateRange,
  getTargetPeriod,
} from './periodUtils';

export interface UserRequirementOverride {
  id: string;
  user_id: string;
  definition_id: string;
  renewal_type: 'none' | 'monthly' | 'yearly' | 'fixed_date' | 'custom_interval';
  renewal_interval: string | null;
  created_at: string;
  created_by_user_id: string | null;
}

export async function fetchOverride(
  userId: string,
  definitionId: string
): Promise<UserRequirementOverride | null> {
  const { data, error } = await supabase
    .from('user_requirement_overrides')
    .select('*')
    .eq('user_id', userId)
    .eq('definition_id', definitionId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching requirement override:', error);
    return null;
  }
  return (data as UserRequirementOverride) || null;
}

export async function fetchOverridesForUser(
  userId: string
): Promise<UserRequirementOverride[]> {
  const { data, error } = await supabase
    .from('user_requirement_overrides')
    .select('*')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching requirement overrides:', error);
    return [];
  }
  return (data as UserRequirementOverride[]) || [];
}

/**
 * Months per period for a renewal type. Returns null for types without a
 * month-based cadence (none, fixed_date), which the auto-fill flow does not support.
 */
export function getIntervalMonths(
  renewalType: string | null | undefined,
  renewalInterval?: string | { months: number } | null
): number | null {
  switch (renewalType) {
    case 'monthly':
      return 1;
    case 'yearly':
      return 12;
    case 'custom_interval':
      return parseIntervalMonths(renewalInterval);
    default:
      return null;
  }
}

/**
 * True when the override has a longer (less frequent) cadence than the
 * definition, which is the only case the phantom auto-fill supports.
 */
export function isOverrideApplicable(
  override: Pick<UserRequirementOverride, 'renewal_type' | 'renewal_interval'>,
  definitionRenewalType: string | null | undefined,
  definitionRenewalInterval?: string | { months: number } | null
): boolean {
  const ovMonths = getIntervalMonths(override.renewal_type, override.renewal_interval);
  const defMonths = getIntervalMonths(definitionRenewalType, definitionRenewalInterval);
  if (ovMonths == null || defMonths == null) return false;
  return ovMonths > defMonths;
}

/**
 * Human label for the effective periodicity, e.g. "Trimestral (personalizado)".
 */
export function getOverrideLabel(
  override: Pick<UserRequirementOverride, 'renewal_type' | 'renewal_interval'>
): string {
  switch (override.renewal_type) {
    case 'monthly':
      return 'Mensual (personalizado)';
    case 'yearly':
      return 'Anual (personalizado)';
    case 'custom_interval': {
      const months = parseIntervalMonths(override.renewal_interval);
      const names: Record<number, string> = {
        2: 'Bimestral',
        3: 'Trimestral',
        4: 'Cuatrimestral',
        6: 'Semestral',
      };
      const name = names[months] || `Cada ${months} meses`;
      return `${name} (personalizado)`;
    }
    default:
      return 'Personalizado';
  }
}

/**
 * Given an approved document's period, returns the 1-based months (same year)
 * whose definition-level periods must be auto-filled with phantom documents.
 *
 * Example: definition monthly, override '3 mons', approved month 1 (January)
 * → override period is Jan–Mar → returns [2, 3].
 *
 * The returned months are definition-level period START months, excluding the
 * period that contains the approved document. Periods are anchored to January
 * (same convention as the rest of the app), so they never cross years.
 * Returns [] when the override is not applicable.
 */
export function getPhantomMonths(
  override: Pick<UserRequirementOverride, 'renewal_type' | 'renewal_interval'>,
  definitionRenewalType: string | null | undefined,
  definitionRenewalInterval: string | { months: number } | null | undefined,
  approvedMonth: number
): number[] {
  if (!isOverrideApplicable(override, definitionRenewalType, definitionRenewalInterval)) {
    return [];
  }

  const ovMonths = getIntervalMonths(override.renewal_type, override.renewal_interval)!;
  const defMonths = getIntervalMonths(definitionRenewalType, definitionRenewalInterval)!;

  const overrideStart = getPeriodStartMonth(approvedMonth, ovMonths);
  const overrideEnd = overrideStart + ovMonths - 1;
  const approvedPeriodStart = getPeriodStartMonth(approvedMonth, defMonths);

  return getExpectedMonthsForYear(definitionRenewalType, definitionRenewalInterval).filter(
    (m) => m >= overrideStart && m <= overrideEnd && m !== approvedPeriodStart
  );
}

const MONTH_ABBREVS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const pad2 = (n: number) => n.toString().padStart(2, '0');

/**
 * After a document is approved, fills the remaining definition-level periods
 * of the user's override period with phantom approved documents, so the rest
 * of the system (progress, reports, renewals) sees every expected period as
 * fulfilled. Mirrors the manual "Aprobar sin documento" flow.
 *
 * Self-contained: fetches document, requirement, definition and override.
 * Returns the number of phantom documents created (0 when no override applies).
 */
export async function autoFillPhantomsAfterApproval(
  documentId: string,
  approverUserId: string | null | undefined
): Promise<number> {
  const { data: doc, error: docError } = await supabase
    .from('documents')
    .select(`
      id,
      applicable_month,
      applicable_year,
      requirement:user_document_requirements!documents_requirement_id_fkey(
        id,
        user_id,
        definition_id,
        due_date,
        definition:document_definitions(id, name, renewal_type, renewal_interval)
      )
    `)
    .eq('id', documentId)
    .single();

  if (docError || !doc) {
    console.error('autoFillPhantoms: document not found', docError);
    return 0;
  }

  const requirement = (doc as any).requirement;
  const definition = requirement?.definition;
  if (!requirement || !definition) return 0;

  const override = await fetchOverride(requirement.user_id, definition.id);
  if (!override) return 0;

  // Determine which period the approved document belongs to
  const { month: approvedMonth, year } = getTargetPeriod({
    applicableMonth: (doc as any).applicable_month,
    applicableYear: (doc as any).applicable_year,
    dueDate: requirement.due_date,
    renewalType: definition.renewal_type,
    renewalInterval: definition.renewal_interval,
  });

  const phantomMonths = getPhantomMonths(
    override,
    definition.renewal_type,
    definition.renewal_interval,
    approvedMonth
  );
  if (phantomMonths.length === 0) return 0;

  const definitionName: string = definition.name || 'DOCUMENTO';
  const docType = definitionName.toUpperCase().replace(/\s+/g, '_');
  const fileExtension = docType.substring(0, 10);
  const folderName = definitionName.toLowerCase().replace(/\s+/g, '_');

  let created = 0;

  for (const month of phantomMonths) {
    try {
      const { startDate, endDate } = getPeriodDateRange(
        definition.renewal_type,
        definition.renewal_interval,
        year,
        month
      );
      const periodStartStr = `${startDate.getFullYear()}-${pad2(startDate.getMonth() + 1)}-${pad2(startDate.getDate())}`;
      const periodEndStr = `${endDate.getFullYear()}-${pad2(endDate.getMonth() + 1)}-${pad2(endDate.getDate())}`;

      // Find an existing requirement for this period (never filter by
      // is_current: upload-flow requirements have is_current=false)
      const { data: existingReqs, error: findError } = await supabase
        .from('user_document_requirements')
        .select('id')
        .eq('user_id', requirement.user_id)
        .eq('definition_id', definition.id)
        .gte('due_date', periodStartStr)
        .lte('due_date', periodEndStr)
        .order('assigned_at', { ascending: true })
        .limit(1);

      if (findError) throw findError;

      let periodRequirementId: string;

      if (existingReqs && existingReqs.length > 0) {
        periodRequirementId = existingReqs[0].id;

        // Skip if the period already has an approved or pending document
        const { data: existingDocs, error: docsError } = await supabase
          .from('documents')
          .select('id, status')
          .eq('requirement_id', periodRequirementId)
          .in('status', ['approved', 'pending_approval'])
          .limit(1);

        if (docsError) throw docsError;
        if (existingDocs && existingDocs.length > 0) continue;
      } else {
        const { data: newReq, error: createError } = await supabase
          .from('user_document_requirements')
          .insert({
            user_id: requirement.user_id,
            definition_id: definition.id,
            status: 'fulfilled',
            due_date: periodEndStr,
            assigned_at: new Date().toISOString(),
            is_current: false,
          })
          .select()
          .single();

        if (createError || !newReq) throw createError;
        periodRequirementId = newReq.id;
      }

      const { error: phantomError } = await supabase.from('documents').insert({
        requirement_id: periodRequirementId,
        file_location: `phantom/${folderName}/${periodRequirementId}/${Date.now()}.${fileExtension}`,
        original_filename: `${docType}_FANTASMA_${MONTH_ABBREVS[month - 1]}_${year}.${fileExtension}`,
        mime_type: 'application/octet-stream',
        file_size_bytes: 0,
        status: 'approved',
        approved_by_user_id: approverUserId || null,
        approval_timestamp: new Date().toISOString(),
        applicable_month: MONTH_ABBREVS[month - 1],
        applicable_year: year,
        issue_date: `${year}-${pad2(month)}-01`,
      } as any);

      if (phantomError) throw phantomError;

      const { error: reqUpdateError } = await supabase
        .from('user_document_requirements')
        .update({ status: 'fulfilled' })
        .eq('id', periodRequirementId);

      if (reqUpdateError) throw reqUpdateError;

      created++;
    } catch (error) {
      console.error(`autoFillPhantoms: error filling month ${month}/${year}:`, error);
    }
  }

  return created;
}
